import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import api, { describeError } from "@/lib/api";
import { toast } from "sonner";

const Field = ({ label, hint, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Input {...props} />
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

export default function Settings() {
  const { user, organization, setUser, checkAuth } = useAuth();
  const isAdmin = user?.role === "firm_admin";

  const [profile, setProfile] = useState({ name: "", mobile: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [savingFirm, setSavingFirm] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    setProfile({ name: user?.name || "", mobile: user?.mobile || "" });
  }, [user]);
  useEffect(() => { setFirmName(organization?.name || ""); }, [organization]);
  useEffect(() => {
    api.get("/notification-preferences").then(({ data }) => setPrefs(data)).catch(() => {});
  }, []);

  const saveProfile = async () => {
    if (!profile.name.trim()) { toast.error("Your name cannot be empty"); return; }
    setSavingProfile(true);
    try {
      const { data } = await api.put("/profile", profile);
      setUser(data.user);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(describeError(e, "Could not save your profile."));
    } finally { setSavingProfile(false); }
  };

  const saveFirm = async () => {
    if (!firmName.trim()) { toast.error("The firm name cannot be empty"); return; }
    setSavingFirm(true);
    try {
      await api.put("/organization", { name: firmName });
      await checkAuth();
      toast.success("Practice details saved");
    } catch (e) {
      toast.error(describeError(e, "Could not save your practice details."));
    } finally { setSavingFirm(false); }
  };

  const changePassword = async () => {
    setPwError("");
    if (pw.new_password.length < 8) { setPwError("Your new password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { setPwError("Both new passwords must match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password, new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) {
      const msg = describeError(e, "Could not change your password.");
      setPwError(msg);
      toast.error(msg);
    } finally { setSavingPw(false); }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/notification-preferences", next); toast.success("Preferences saved"); }
    catch { setPrefs(prefs); toast.error("Could not save preferences"); }
  };

  const PREFS = [["hearing", "Hearing reminders"], ["task", "Task reminders"],
                 ["case", "Case activity"], ["client", "Client updates"]];

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage your profile and practice preferences." />
      <Tabs defaultValue="profile">
        <TabsList className="mb-4 flex-wrap bg-secondary/60">
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="practice" data-testid="settings-tab-practice">Practice</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="max-w-2xl border-border p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Full name" value={profile.name} data-testid="settings-name-input"
                     onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} />
              <Field label="Mobile" value={profile.mobile} data-testid="settings-mobile-input"
                     onChange={(e) => setProfile((p) => ({ ...p, mobile: e.target.value }))} />
              <Field label="Email" value={user?.email || ""} disabled data-testid="settings-email-input"
                     hint="Your sign-in email cannot be changed here." />
              <Field label="Role" value={(user?.role || "").replace("_", " ")} disabled
                     data-testid="settings-role-input" hint="Only a firm admin can change roles, from Team." />
            </div>
            <div className="mt-6">
              <Button onClick={saveProfile} disabled={savingProfile} data-testid="settings-save-profile-btn"
                      className="bg-navy text-white hover:bg-navy-light">
                {savingProfile ? "Saving…" : "Save profile"}
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="practice">
          <Card className="max-w-2xl border-border p-6 shadow-sm">
            {isAdmin ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Practice / firm name" value={firmName} data-testid="settings-firm-name-input"
                         onChange={(e) => setFirmName(e.target.value)} />
                  <Field label="Account type" disabled data-testid="settings-org-type-input"
                         value={organization?.type === "law_firm" ? "Law firm" : "Individual advocate"} />
                  <Field label="Current plan" value={organization?.plan || ""} disabled
                         data-testid="settings-org-plan-input" hint="Change your plan from Subscription." />
                </div>
                <div className="mt-6">
                  <Button onClick={saveFirm} disabled={savingFirm} data-testid="settings-save-firm-btn"
                          className="bg-navy text-white hover:bg-navy-light">
                    {savingFirm ? "Saving…" : "Save practice details"}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="settings-practice-readonly">
                Practice details are managed by your firm admin.
              </p>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="max-w-2xl border-border p-6 shadow-sm">
            {!prefs ? <p className="text-sm text-muted-foreground">Loading preferences…</p> : (
              <div className="space-y-4">
                {PREFS.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <Label className="text-sm text-navy">{label}</Label>
                    <Switch checked={prefs[key] !== false} onCheckedChange={() => togglePref(key)}
                            data-testid={`settings-pref-${key}`} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card className="max-w-2xl border-border p-6 shadow-sm">
            <h3 className="font-display text-xl text-navy">Change password</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              You will stay signed in on this device.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4">
              <Field label="Current password" type="password" value={pw.current_password}
                     data-testid="settings-current-password-input"
                     onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} />
              <Field label="New password" type="password" value={pw.new_password}
                     data-testid="settings-new-password-input" hint="At least 8 characters."
                     onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} />
              <Field label="Confirm new password" type="password" value={pw.confirm}
                     data-testid="settings-confirm-password-input"
                     onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />
            </div>
            {pwError && (
              <p role="alert" data-testid="settings-password-error" className="mt-3 text-sm text-destructive">
                {pwError}
              </p>
            )}
            <div className="mt-6">
              <Button onClick={changePassword} disabled={savingPw} data-testid="settings-change-password-btn"
                      className="bg-navy text-white hover:bg-navy-light">
                {savingPw ? "Updating…" : "Change password"}
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
